import type { OnlyRequire } from '@openx/types/helpers';
import {
  type LalSegmentApi,
  LalSegmentFields,
  type LalSegmentFormState,
  type ListSegment,
} from '@openx/types/segments';
import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { mapLalSeedSourceToSeedListData } from '../utils/mapLalSeedSource';
import { mapListSegmentToSeedListData } from '../utils/mapListSegment';

type UseAsTemplateState = { useAsTemplate: boolean; lalSegment: OnlyRequire<LalSegmentApi, 'provider'> };
type SegmentsState = { segments: ListSegment[] };
type LocationState = UseAsTemplateState | SegmentsState | null;

const isUseAsTemplate = (state?: LocationState | null): state is UseAsTemplateState =>
  !!state && 'useAsTemplate' in state;
const isCreateFromAudienceSegments = (state?: LocationState | null): state is SegmentsState =>
  !!state && 'segments' in state;

export const useInitialValues = (
  accountId = '',
  existingLalSegment?: Partial<LalSegmentApi> | null
): Partial<LalSegmentFormState> => {
  const location = useLocation();
  const state = structuredClone<LocationState | undefined>(location.state);

  return useMemo(() => {
    // If no lalSegment is provided (LAL Create), we return an empty initial state
    if (!existingLalSegment) {
      const initialLal: Partial<LalSegmentFormState> = {
        [LalSegmentFields.ACCOUNT_ID]: accountId,
        [LalSegmentFields.SEEDS]: [],
        [LalSegmentFields.SEED_NAME]: '',
        [LalSegmentFields.NAME]: '',
      };

      if (isCreateFromAudienceSegments(state)) {
        initialLal[LalSegmentFields.PROVIDER] = state.segments[0].provider;
        initialLal[LalSegmentFields.SEEDS] = mapListSegmentToSeedListData(state.segments, true);
      }

      if (isUseAsTemplate(state)) {
        const { lalSegment } = state;
        const { account, provider } = lalSegment;

        initialLal[LalSegmentFields.ACCOUNT_ID] = account?.id ?? accountId;
        initialLal[LalSegmentFields.PROVIDER] = provider;
        initialLal[LalSegmentFields.SEEDS] = mapLalSeedSourceToSeedListData({ ...lalSegment });
      }

      return initialLal;
    }

    // If lalSegment is provided (LAL Edit), we map its properties to the initial values
    return {
      [LalSegmentFields.ACCOUNT_ID]: existingLalSegment.account?.id ?? '',
      [LalSegmentFields.SEEDS]: mapLalSeedSourceToSeedListData(existingLalSegment),
      [LalSegmentFields.SEED_NAME]: existingLalSegment.lookalike_seeds_aggregate_name ?? '',
      [LalSegmentFields.NAME]: existingLalSegment.lookalike_name,
      [LalSegmentFields.PROVIDER]: existingLalSegment.provider,
      [LalSegmentFields.ESTIMATED_REACH]: existingLalSegment.seeds_estimated_reach ?? 0,
    };
  }, [accountId, existingLalSegment, state]);
};
